/** @file "Keep image in place": new uvs for moved rest vertices through the mesh's pixel → rest-space fit. */
import { meshPixelToLocal } from '@pose2d/core/load/meshPixelToLocal';
import type { ImageDoc, MeshAtt } from '@pose2d/format/types';
import { affine } from '@pose2d/math/affine';
import { clamp } from '@pose2d/math/clamp';
import type { Vec2 } from '@pose2d/math/types';

/**
 * uvs where each vertex in `moved` samples the image under its new rest position `rest` (the least-squares pixel → rest
 * fit of the mesh's current uvs and positions, core meshPixelToLocal), clamped to 0..1; other uvs unchanged.
 */
export function followUvs(mesh: MeshAtt, image: Pick<ImageDoc, 'w' | 'h'>, before: readonly number[], rest: readonly number[], moved: readonly number[]): number[] {
  const toPixel = affine.invert(new Float64Array(6), meshPixelToLocal(mesh.uvs, image.w, image.h, before));
  const uvs = [...mesh.uvs];
  const p: Vec2 = [0, 0];
  for (const v of moved) {
    affine.apply(toPixel, rest[2 * v] ?? 0, rest[2 * v + 1] ?? 0, p);
    uvs[2 * v] = clamp(p[0] / image.w, 0, 1);
    uvs[2 * v + 1] = clamp(p[1] / image.h, 0, 1);
  }
  return uvs;
}
